import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddressScreen, NamesScreen, PhoneScreen } from './components';
import {
  CreateUserMutationVariables,
  useCreateUserMutation,
} from './createUser.generated';
import { IFormInput } from './types';
import cslx from 'clsx';
import { Button } from '@web/utils';
import { useAppDispatch } from '@/store/hooks';
import { setIsRetrieved } from '@/store/slices/userSlice';
import { Logo } from '../Logo';

export enum FormStage {
  Names = 'Names',
  Phone = 'Phone',
  Address = 'Address',
}

const initialValues = {
  givenName: '',
  familyName: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressPostcode: '',
  addressLocality: '',
  userMarketing: false,
  userTerms: false,
};

export default function CreateUserModal() {
  const [currentStage, setCurrentStage] = useState<FormStage>(FormStage.Names);
  const stagesOrder = [FormStage.Names, FormStage.Phone, FormStage.Address];
  const [isLoading, setLoading] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState({ lat: 0, long: 0 });
  const [, createUser] = useCreateUserMutation();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm<IFormInput>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const formValues = watch();

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setLoading(true);

    const user: CreateUserMutationVariables = {
      user: {
        familyName: data.familyName,
        givenName: data.givenName,
        phoneNumber: data.phoneNumber,
        language: i18n.language || 'de',
        emailMarketing: data.userMarketing,
        primaryAddress: {
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2,
          addressPostcode: data.addressPostcode,
          addressLocality: data.addressLocality,
          coordinates,
        },
      },
    };

    try {
      const result = await createUser(user);
      if (result.error) {
        alert(result.error.message);
      } else {
        reset(initialValues);
        dispatch(setIsRetrieved(true));
        window.location.reload();
      }
    } catch (error) {
      console.log('error from mutation', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (
    e: React.MouseEvent,
    direction: 'next' | 'previous' = 'next',
  ) => {
    e.preventDefault();

    if (direction === 'next') {
      // Validate current stage
      const isValid = await validateCurrentStage();
      if (!isValid) return;

      if (currentStage === FormStage.Address) {
        handleSubmit(onSubmit)();
        return;
      }
    }

    const currentIndex = stagesOrder.indexOf(currentStage);
    if (direction === 'next' && currentIndex < stagesOrder.length - 1) {
      setCurrentStage(stagesOrder[currentIndex + 1]);
    } else if (direction === 'previous' && currentIndex > 0) {
      setCurrentStage(stagesOrder[currentIndex - 1]);
    }
  };

  const validateCurrentStage = async () => {
    switch (currentStage) {
      case FormStage.Names:
        return await trigger(['givenName', 'familyName', 'userTerms']);
      case FormStage.Phone:
        return await trigger(['phoneNumber']);
      case FormStage.Address:
        return await trigger([
          'addressLine1',
          'addressPostcode',
          'addressLocality',
        ]);
      default:
        return true;
    }
  };

  const handleSkip = () => {
    if (currentStage === FormStage.Phone) {
      setCurrentStage(FormStage.Address);
    } else {
      handleSubmit(onSubmit)();
    }
  };

  return (
    <div
      className={cslx(
        'flex absolute top-0 left-0 z-100 min-h-100vh w-100-vw bg-grey-400 bg-opacity-40',
      )}
    >
      <form className="p-2-0 md:p-5-0 mx-auto my-auto w-full max-w-[500px] rounded-14-0 border border-grey-300 bg-white shadow md:min-h-[80vh] flex flex-col transition-transform duration-200">
        <div className="mb-1-6 flex justify-between items-center">
          <Logo type={2} />
          {currentStage === FormStage.Phone && (
            <span
              className="font-bd text-2-0 leading-1-0 text-primary-grey cursor-pointer"
              onClick={handleSkip}
              role="skip"
            >
              {t('global.skip')}
            </span>
          )}
        </div>
        {currentStage === FormStage.Names && (
          <NamesScreen
            register={register}
            errors={errors}
            setValue={setValue}
            formValues={formValues}
          />
        )}
        {currentStage === FormStage.Phone && (
          <PhoneScreen
            register={register}
            errors={errors}
            setValue={setValue}
            formValues={formValues}
            handleStageChange={handleStageChange}
          />
        )}
        {currentStage === FormStage.Address && (
          <AddressScreen
            register={register}
            errors={errors}
            setValue={setValue}
            formValues={formValues}
            handleStageChange={handleStageChange}
            setCoordinates={setCoordinates}
          />
        )}

        <div className="pt-4-0 mt-auto">
          <Button
            className="w-full"
            onClick={(e) => handleStageChange(e)}
            disabled={isLoading}
          >
            {t('global.next')}
          </Button>
        </div>
      </form>
    </div>
  );
}
